import StudentEvals from "@/components/app/StudentEvals";
import { PenLine, Trophy, Award } from "lucide-react";
import StudentBadges from "@/components/app/StudentBadges";
import { StudentLive } from "@/components/app/EvalLive";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import StudentNotebook from "@/components/app/StudentNotebook";
import { StudentResourcesPanel, StudentTemarioPanel } from "@/components/app/StudentResourcesTemario";
import LogoSplash from "@/components/app/LogoSplash";
import NotificationCenter from "@/components/app/NotificationCenter";
import PushPrompt from "@/components/app/PushPrompt";
import ThemedBackground from "@/components/app/ThemedBackground";
import { applyTheme, resetThemeToStock } from "@/lib/themes";
import {
  LogOut, ArrowLeft, GraduationCap, BookOpen, Gamepad2, NotebookPen,
  MessageCircle, ClipboardCheck, Loader2, Paperclip, Send, X, FileText, Download,
  CalendarClock, Megaphone, FolderOpen, ExternalLink, Youtube, Sparkles, CheckCircle2,
} from "lucide-react";

const LS_TOKEN = "uaicard_student_token";
const ytId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/); return m ? m[1] : null; };
const dueLabel = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d) ? null : d.toLocaleString("es-MX", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); };
const TYPE_META = {
  tarea: { label: "Tarea", icon: ClipboardCheck, badge: "bg-indigo-600 text-white" },
  anuncio: { label: "Aviso", icon: Megaphone, badge: "bg-amber-500 text-white" },
  material: { label: "Material", icon: FolderOpen, badge: "bg-emerald-600 text-white" },
};
const SUB_STATE = {
  entregado: { label: "Entregada ✓", cls: "bg-emerald-100 text-emerald-700" },
  revisado: { label: "Revisada", cls: "bg-sky-100 text-sky-700" },
  calificado: { label: "Calificada", cls: "bg-indigo-100 text-indigo-700" },
};

/* ---------------- Login ---------------- */
function StudentLogin({ onDone }) {
  const [uid, setUid] = useState("");
  const [busy, setBusy] = useState(false);
  const cachedTheme = (() => { try { return JSON.parse(localStorage.getItem("uai_alumno_theme") || "null"); } catch { return null; } })();
  useEffect(() => { try { resetThemeToStock(); } catch { /* noop */ } }, []);
  useEffect(() => {
    const demoUid = new URLSearchParams(window.location.search).get("demo");
    if (!demoUid) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/parent/verify", { uid: demoUid.trim() });
        localStorage.setItem(LS_TOKEN, data.token);
        onDone(data.token);
      } catch (e) { toast.error("No se pudo abrir la demo del alumno"); setBusy(false); }
    })();
  }, []); // eslint-disable-line
  const submit = async () => {
    if (!uid.trim()) { toast.error("Escribe tu UID"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/parent/verify", { uid: uid.trim() });
      localStorage.setItem(LS_TOKEN, data.token);
      onDone(data.token);
    } catch (e) { toast.error(e.response?.data?.detail || "UID no encontrado"); }
    finally { setBusy(false); }
  };
  return (
    <div className="h-screen overflow-hidden bg-nardo-50 relative flex flex-col" data-testid="student-login-screen">
      <ThemedBackground bgId={cachedTheme?.theme_bg} speed={cachedTheme?.theme_bg_speed} />
      <LogoSplash sessionKey="alumno" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="h-[3px] w-full bg-navy shrink-0" />
        <header className="max-w-5xl w-full mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/zavia-icon.png" alt="ZAVIA" data-testid="zavia-icon" className="w-9 h-9 object-contain shrink-0" />
            <div className="leading-none min-w-0">
              <div className="font-display font-extrabold tracking-tight text-[15px] text-navy truncate">Portal del Alumno</div>
              <div className="font-display tracking-[0.3em] text-[10px] uppercase text-nardo-500">ZAVIA</div>
            </div>
          </div>
          <Link to="/" data-testid="student-back-home" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy">
            <ArrowLeft className="w-3.5 h-3.5" /> Inicio
          </Link>
        </header>
        <main className="flex-1 min-h-0 flex items-center justify-center px-5 sm:px-8 pb-6">
          <div className="relative bg-white border border-nardo-200 rounded-xl p-6 sm:p-8 shadow-lg max-w-md w-full" data-testid="student-login">
              <div className="absolute -top-px left-0 right-0 h-[2px] bg-navy" />
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="w-4 h-4 text-navy" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-navy">Acceso de alumno</div>
              </div>
              <h2 className="font-display font-bold text-2xl text-navy mb-1">Portal del Alumno</h2>
              <p className="text-sm text-nardo-500 mb-6">Ingresa el <strong>UID</strong> de tu credencial. Te lo proporciona el colegio.</p>
              <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">UID del alumno</label>
                  <input
                    data-testid="student-uid-input"
                    value={uid}
                    onChange={(e) => setUid(e.target.value.toUpperCase())}
                    placeholder="EJ. A200"
                    className="mt-1 w-full text-center font-mono tracking-[0.3em] text-2xl uppercase bg-nardo-50 border-2 border-dashed border-nardo-300 p-4 rounded-lg focus:border-navy outline-none"
                  />
                </div>
                <button type="submit" disabled={busy || !uid} data-testid="student-login-btn"
                  className="w-full bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <GraduationCap className="w-5 h-5" />} Entrar
                </button>
              </form>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------------- Tareas ---------------- */
function TaskCard({ post, token, onChanged }) {
  const meta = TYPE_META[post.type] || TYPE_META.tarea;
  const due = dueLabel(post.due_at);
  const sub = post.submission;
  const graded = sub?.status === "calificado";
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(sub?.note || "");
  const fileRef = useRef(null);
  const H = { "X-Parent-Token": token };

  const submit = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      await api.post(`/parent/aula/submit/${post.id}`, fd, { headers: { ...H, "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("¡Tarea entregada!"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo entregar"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeFile = async (t) => {
    setBusy(true);
    try { await api.delete(`/parent/aula/submit/${post.id}/file/${t}`, { headers: H }); toast.success("Archivo quitado"); onChanged(); }
    catch { toast.error("No se pudo quitar"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4" data-testid={`student-post-${post.id}`}>
      <div className="flex items-start gap-2">
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}><meta.icon className="w-3 h-3" /> {meta.label}</span>
        <div className="flex-1 min-w-0"><div className="font-bold text-navy">{post.title}</div><div className="text-[11px] text-nardo-400">{post.docente_name} · {(post.created_at || "").slice(0, 10)}</div></div>
      </div>
      {post.description && <p className="text-sm text-nardo-600 mt-2 whitespace-pre-wrap leading-relaxed">{post.description}</p>}
      {due && <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1"><CalendarClock className="w-3.5 h-3.5" /> Entrega: {due}</div>}
      {post.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {post.attachments.map((a, i) => <a key={i} href={a.href ? `${API}${a.href}` : `${API}/aula/archivo/${a.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg px-2.5 py-1.5"><FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[10rem]">{a.name}</span> <Download className="w-3 h-3" /></a>)}
        </div>
      )}
      {post.links?.length > 0 && <div className="flex flex-col gap-1 mt-2">{post.links.map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:underline break-all"><ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l}</a>)}</div>}
      {post.youtube?.length > 0 && <div className="grid sm:grid-cols-2 gap-3 mt-2">{post.youtube.map((y, i) => { const id = ytId(y); return id ? <div key={i} className="aspect-video rounded-xl overflow-hidden bg-black/5"><iframe title={`yt-${i}`} src={`https://www.youtube.com/embed/${id}`} className="w-full h-full" allowFullScreen frameBorder="0" /></div> : <a key={i} href={y} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:underline"><Youtube className="w-4 h-4" /> {y}</a>; })}</div>}

      {post.type === "tarea" && (
        <div className="mt-3 pt-3 border-t border-nardo-100">
          <div className="flex items-center justify-between mb-2"><span className="text-[11px] uppercase tracking-[0.18em] text-indigo-500 font-semibold">Tu entrega</span>{sub?.status && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SUB_STATE[sub.status]?.cls || "bg-nardo-100 text-nardo-600"}`} data-testid={`student-sub-status-${post.id}`}>{SUB_STATE[sub.status]?.label || sub.status}</span>}</div>
          {sub?.files?.length > 0 && <div className="flex flex-wrap gap-2 mb-2">{sub.files.map((f, i) => <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-nardo-100 text-navy rounded-lg px-2.5 py-1.5"><FileText className="w-3.5 h-3.5" /><a href={`${API}/aula/archivo/${f.token}`} target="_blank" rel="noreferrer" className="truncate max-w-[9rem] hover:underline">{f.name}</a>{!graded && <button onClick={() => removeFile(f.token)} disabled={busy} className="text-nardo-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}</span>)}</div>}
          {graded ? (
            <div className="rounded-lg bg-indigo-50 p-3">{sub.grade != null && <div className="text-sm font-bold text-indigo-700">Calificación: {sub.grade}</div>}{sub.feedback && <div className="text-[12px] text-nardo-600 mt-1">Comentario: {sub.feedback}</div>}</div>
          ) : (
            <div className="space-y-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota para tu maestro (opcional)…" data-testid={`student-note-${post.id}`} className="w-full text-sm border border-nardo-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 resize-none" />
              <div className="flex flex-wrap gap-2">
                <label className="text-xs inline-flex items-center gap-1.5 text-indigo-700 border border-indigo-300 hover:bg-indigo-600 hover:text-white rounded-lg px-3 py-2 cursor-pointer transition-colors">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Subir archivo<input ref={fileRef} type="file" className="hidden" data-testid={`student-file-${post.id}`} onChange={(e) => e.target.files?.[0] && submit(e.target.files[0])} /></label>
                <button onClick={() => submit(null)} disabled={busy || (!note.trim() && !sub?.files?.length)} data-testid={`student-submit-${post.id}`} className="text-xs inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50"><Send className="w-4 h-4" /> Entregar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TareasPanel({ token }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true; setLoading(true);
    api.get("/parent/aula/posts", { headers: { "X-Parent-Token": token } })
      .then(({ data }) => { if (alive) setPosts(data.posts || []); })
      .catch(() => { if (alive) setPosts([]); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);
  if (loading) return <div className="py-16 text-center text-nardo-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (posts.length === 0) return <div className="py-16 text-center text-nardo-500" data-testid="student-tareas-empty">Todavía no hay tareas ni avisos de tu grupo.</div>;
  return <div className="space-y-3">{posts.map((p) => <TaskCard key={p.id} post={p} token={token} onChanged={() => setReload((k) => k + 1)} />)}</div>;
}

function ComingSoon({ icon: Icon, title, desc }) {
  return (
    <div className="py-16 text-center" data-testid="student-coming-soon">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center mx-auto mb-3"><Icon className="w-8 h-8" /></div>
      <h3 className="font-display font-bold text-navy text-lg">{title}</h3>
      <p className="text-sm text-nardo-500 mt-1 max-w-md mx-auto">{desc}</p>
      <span className="inline-block mt-3 text-[11px] uppercase tracking-widest bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">Próximamente</span>
    </div>
  );
}

const MODULES = [
  { id: "tareas", label: "Tareas", icon: ClipboardCheck, color: "from-indigo-500 to-violet-600" },
  { id: "examenes", label: "Exámenes", icon: PenLine, color: "from-violet-500 to-purple-600" },
  { id: "concurso", label: "Concurso", icon: Trophy, color: "from-amber-400 to-orange-500" },
  { id: "insignias", label: "Mis medallas", icon: Award, color: "from-amber-400 to-yellow-500" },
  { id: "temario", label: "Temario", icon: CalendarClock, color: "from-blue-500 to-indigo-600" },
  { id: "recursos", label: "Recursos", icon: FolderOpen, color: "from-teal-500 to-emerald-600" },
  { id: "cuaderno", label: "Cuaderno digital", icon: NotebookPen, color: "from-sky-500 to-cyan-600" },
  { id: "libros", label: "Libros digitales", icon: BookOpen, color: "from-emerald-500 to-teal-600" },
  { id: "juegos", label: "Juegos escolares", icon: Gamepad2, color: "from-orange-500 to-red-500" },
  { id: "mensajes", label: "Mensajes", icon: MessageCircle, color: "from-fuchsia-500 to-pink-600" },
];

/* ---------------- Biblioteca y Juegos ---------------- */
const RES_COLORS = [
  "from-emerald-500 to-teal-600", "from-sky-500 to-cyan-600", "from-indigo-500 to-violet-600",
  "from-orange-500 to-amber-600", "from-fuchsia-500 to-pink-600", "from-rose-500 to-red-600",
];
function ResourcesPanel({ token, kind }) {
  const [items, setItems] = useState(null);
  const [level, setLevel] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/student/resources", { headers: { "X-Parent-Token": token } })
      .then(({ data }) => { if (alive) { setItems(kind === "libros" ? data.books : data.games); setLevel(data.level); } })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [token, kind]);
  const Icon = kind === "libros" ? BookOpen : Gamepad2;
  if (items === null) return <div className="py-16 text-center text-nardo-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 ${kind === "libros" ? "text-emerald-600" : "text-orange-500"}`} />
        <h2 className="font-display font-bold text-lg text-navy">{kind === "libros" ? "Libros digitales" : "Juegos escolares"}</h2>
        {level && <span className="ml-auto text-[11px] bg-nardo-100 text-nardo-600 px-2.5 py-1 rounded-full font-semibold">Nivel: {level}</span>}
      </div>
      {items.length === 0 ? (
        <div className="py-16 text-center text-nardo-500" data-testid={`student-${kind}-empty`}>Aún no hay {kind === "libros" ? "libros" : "juegos"} para tu nivel.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`student-${kind}-grid`}>
          {items.map((r, i) => (
            <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`student-resource-${r.id}`}
              className="group bg-white rounded-2xl border border-nardo-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">
              <div className={`h-20 bg-gradient-to-br ${RES_COLORS[i % RES_COLORS.length]} flex items-center justify-center`}>
                <Icon className="w-8 h-8 text-white/90" />
              </div>
              <div className="p-3">
                <div className="font-bold text-navy text-sm leading-tight flex items-center gap-1.5">{r.title}<ExternalLink className="w-3.5 h-3.5 text-nardo-400 group-hover:text-indigo-600 shrink-0" /></div>
                {r.desc && <div className="text-[12px] text-nardo-500 mt-1 line-clamp-2">{r.desc}</div>}
                <span className="inline-block mt-2 text-[10px] uppercase tracking-wide bg-nardo-100 text-nardo-500 px-2 py-0.5 rounded-full font-semibold">{r.level}</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Mensajes con el docente ---------------- */
function StudentMessages({ token }) {
  const [teachers, setTeachers] = useState(null);
  const [active, setActive] = useState(null); // {id,name}
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const H = { "X-Parent-Token": token };

  const loadTeachers = () => api.get("/student/messages", { headers: H }).then(({ data }) => setTeachers(data.teachers || [])).catch(() => setTeachers([]));
  useEffect(() => { loadTeachers(); }, []); // eslint-disable-line

  const loadThread = (t) => { setActive(t); api.get(`/student/messages/${t.id}`, { headers: H }).then(({ data }) => setMsgs(data.messages || [])).catch(() => setMsgs([])); };
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => api.get(`/student/messages/${active.id}`, { headers: H }).then(({ data }) => setMsgs(data.messages || [])).catch(() => {}), 5000);
    return () => clearInterval(iv);
  }, [active]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setBusy(true); setText("");
    try { const { data } = await api.post(`/student/messages/${active.id}`, { text: t }, { headers: H }); setMsgs((m) => [...m, data]); }
    catch { toast.error("No se pudo enviar"); setText(t); } finally { setBusy(false); }
  };

  if (teachers === null) return <div className="py-16 text-center text-nardo-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  if (active) {
    return (
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm flex flex-col h-[65vh]" data-testid="student-chat-thread">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-nardo-100 bg-fuchsia-50 rounded-t-2xl">
          <button onClick={() => setActive(null)} data-testid="student-chat-back" className="text-nardo-500 hover:text-navy"><ArrowLeft className="w-5 h-5" /></button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center font-bold">{(active.name || "P").slice(0, 1)}</div>
          <div><div className="font-bold text-navy text-sm">Prof. {active.name}</div>{active.subject && <div className="text-[11px] text-nardo-400">{active.subject}</div>}</div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-nardo-50/40">
          {msgs.length === 0 && <div className="text-center text-nardo-400 text-sm py-10">Escríbele a tu maestro para dudas de tus tareas 😊</div>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "student" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${m.sender === "student" ? "bg-fuchsia-600 text-white rounded-br-sm" : "bg-white border border-nardo-200 text-navy rounded-bl-sm"}`}>{m.text}<div className={`text-[9px] mt-1 text-right ${m.sender === "student" ? "text-white/70" : "text-nardo-400"}`}>{m.created_at ? new Date(m.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div></div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-nardo-100 flex items-center gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }} rows={2} placeholder="Escribe un mensaje…" data-testid="student-chat-input" className="flex-1 border border-nardo-200 rounded-2xl px-4 py-2.5 text-base resize-none max-h-32 outline-none focus:border-fuchsia-500" />
          <button onClick={send} disabled={busy || !text.trim()} data-testid="student-chat-send" className="w-10 h-10 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white flex items-center justify-center disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4"><MessageCircle className="w-5 h-5 text-fuchsia-600" /><h2 className="font-display font-bold text-lg text-navy">Mensajes con tus maestros</h2></div>
      {teachers.length === 0 ? (
        <div className="py-16 text-center text-nardo-500" data-testid="student-messages-empty">Todavía no tienes maestros asignados a tu grupo.</div>
      ) : (
        <div className="space-y-2" data-testid="student-teachers-list">
          {teachers.map((t) => (
            <button key={t.id} onClick={() => loadThread(t)} data-testid={`student-teacher-${t.id}`} className="w-full flex items-center gap-3 bg-white rounded-2xl border border-nardo-200 shadow-sm hover:shadow-md p-3 text-left transition-all">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center font-bold shrink-0">{(t.name || "P").slice(0, 1)}</div>
              <div className="flex-1 min-w-0"><div className="font-bold text-navy text-sm truncate">Prof. {t.name}</div>{t.subject && <div className="text-[11px] text-nardo-400 truncate">{t.subject}</div>}</div>
              {t.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">{t.unread}</span>}
              <Send className="w-4 h-4 text-nardo-300 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PortalAlumno() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [home, setHome] = useState(null);
  const [tab, setTab] = useState("tareas");
  const [loading, setLoading] = useState(!!token);
  const [theme, setTheme] = useState(() => { try { return JSON.parse(localStorage.getItem("uai_alumno_theme") || "null"); } catch { return null; } });

  const loadHome = (t) => {
    setLoading(true);
    api.get("/student/home", { headers: { "X-Parent-Token": t } })
      .then(({ data }) => setHome(data))
      .catch(() => { localStorage.removeItem(LS_TOKEN); setToken(""); setHome(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (token) loadHome(token); }, [token]); // eslint-disable-line

  // Tema del colegio (colores + fondo animado guardados por el usuario/correo registrado).
  useEffect(() => {
    if (!token) return;
    let on = true;
    api.get("/parent/me", { headers: { "X-Parent-Token": token } })
      .then((r) => { if (on) { const th = { ...(r.data.theme || {}), watermark_url: r.data.watermark_url, logo_url: r.data.logo_url }; setTheme(th); try { localStorage.setItem("uai_alumno_theme", JSON.stringify(th)); } catch { /* noop */ } } })
      .catch(() => {});
    return () => { on = false; };
  }, [token]);
  useEffect(() => {
    const t = theme || {};
    applyTheme(t.theme_palette, t.theme_btn, {
      title: t.theme_title_color, body: t.theme_text_color, btn: t.theme_btn_text_color,
    }, t.theme_logo_colors, t.theme_panel_color, t.theme_custom_color);
  }, [theme]);

  const logout = () => { localStorage.removeItem(LS_TOKEN); setToken(""); setHome(null); };

  if (!token) return <StudentLogin onDone={(t) => setToken(t)} />;
  if (loading || !home) return <div className="min-h-screen flex items-center justify-center bg-nardo-50"><Loader2 className="w-8 h-8 animate-spin text-navy" /></div>;

  const s = home.student;
  const photoUrl = s.has_photo ? `${API}/student/photo?token=${encodeURIComponent(token)}` : null;

  return (
    <div className="min-h-screen bg-nardo-50 relative" data-testid="student-portal">
      <LogoSplash sessionKey="alumno" />
      <ThemedBackground bgId={theme?.theme_bg} speed={theme?.theme_bg_speed} watermarkUrl={theme && theme.watermark_url ? `${API}${theme.watermark_url}` : null} />
      <div className="relative z-10">
      <div className="h-[3px] w-full bg-navy" />
      {/* Header */}
      <div className="bg-navy text-white shadow-lg">
        {(theme?.logo_url && theme?.logo_show_name && (theme?.school_name || home?.school?.name)) && (
          <div className="max-w-5xl mx-auto px-4 pt-4 flex items-center gap-2.5" data-testid="student-school-brand">
            <img src={`${API}${theme.logo_url}`} alt="" crossOrigin="anonymous" className="h-8 w-auto max-w-[120px] object-contain bg-white/95 rounded-md px-1.5 py-1" />
            <span className="font-display font-bold text-base sm:text-lg truncate">{theme?.school_name || home?.school?.name}</span>
          </div>
        )}
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-4">
          {photoUrl ? <img src={photoUrl} alt={s.full_name} className="w-16 h-16 rounded-2xl object-cover border-2 border-white/40 shadow-lg" data-testid="student-photo" />
            : <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center text-2xl font-extrabold border-2 border-white/30">{(s.full_name || "A").slice(0, 1)}</div>}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Portal del Alumno · {theme?.portal_subtitle || "ZAVIA"}</div>
            <div className="font-display font-extrabold text-xl sm:text-2xl leading-tight truncate" data-testid="student-name">{s.full_name}</div>
            <div className="text-sm text-white/75 truncate">{home.school.name} · {s.group_label}{home.teachers[0] ? ` · Prof. ${home.teachers[0].name}` : ""}</div>
            {s.plan_label && <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold bg-white/15 text-white px-2.5 py-1 rounded-full" data-testid="student-plan-badge">Plan: {s.plan_label}</span>}
          </div>
          <div className="shrink-0 flex items-center gap-1">
            <NotificationCenter parentToken={token} tone="dark" />
            <button onClick={logout} data-testid="student-logout" className="inline-flex items-center gap-1.5 text-xs bg-white/15 hover:bg-white/25 px-3 py-2 rounded-lg transition-colors"><LogOut className="w-4 h-4" /> Salir</button>
          </div>
        </div>
      </div>

      {/* Module tabs */}
      <div className="max-w-5xl mx-auto px-4 -mt-4">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 bg-white rounded-2xl shadow-lg border border-nardo-200 p-2">
          {MODULES.map((m) => {
            const activeTab = tab === m.id;
            return (
              <button key={m.id} onClick={() => setTab(m.id)} data-testid={`student-tab-${m.id}`}
                className={`relative rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all ${activeTab ? "bg-navy text-white shadow-md" : "text-navy hover:bg-nardo-50"}`}>
                <m.icon className={`w-6 h-6 ${activeTab ? "text-white" : "text-navy"}`} />
                <span className="text-[11px] font-semibold text-center leading-tight">{m.label}</span>
                {m.id === "tareas" && home.stats.pending_tasks > 0 && <span className="absolute top-1 right-2 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{home.stats.pending_tasks}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <PushPrompt parentToken={token} />
        {tab === "tareas" && (
          <div>
            <div className="flex items-center gap-2 mb-4"><ClipboardCheck className="w-5 h-5 text-navy" /><h2 className="font-display font-bold text-lg text-navy">Mis tareas y avisos</h2>
              {home.stats.pending_tasks === 0 && home.stats.total_tasks > 0 && <span className="ml-auto text-xs inline-flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 className="w-4 h-4" /> ¡Al corriente!</span>}
            </div>
            <TareasPanel token={token} />
          </div>
        )}
        {tab === "cuaderno" && <StudentNotebook token={token} studentName={s.full_name} />}
        {tab === "temario" && <StudentTemarioPanel token={token} />}
        {tab === "recursos" && <StudentResourcesPanel token={token} />}
        {tab === "libros" && <ResourcesPanel token={token} kind="libros" />}
        {tab === "juegos" && <ResourcesPanel token={token} kind="juegos" />}
        {tab === "mensajes" && <StudentMessages token={token} />}
        {tab === "examenes" && <StudentEvals token={token} />}
        {tab === "concurso" && <StudentLive token={token} />}
        {tab === "insignias" && <StudentBadges token={token} />}
      </div>
      <footer className="max-w-5xl mx-auto px-4 pb-10 text-center">
        <a href="/privacidad-alumno" data-testid="student-privacy-link" className="inline-flex items-center gap-1.5 text-xs text-nardo-500 hover:text-navy underline underline-offset-2">
          Aviso de Privacidad
        </a>
      </footer>

      </div>
    </div>
  );
}
